import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatDistanceToNow } from 'date-fns';
import { ExternalLink } from 'lucide-react';

interface QueueItem {
  id: string;
  paper_id: string;
  paper_url: string;
  status: string;
  retry_count: number;
  max_retries: number;
  scheduled_at: string;
  processed_at: string | null;
  error_message: string | null;
  created_at: string;
  papers: { id: string; title: string } | null;
}

interface QueueTableProps {
  items: QueueItem[] | undefined;
}

export const QueueTable = ({ items }: QueueTableProps) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!items || items.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No items in queue
      </div>
    );
  }

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Paper</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Retries</TableHead>
            <TableHead>Scheduled</TableHead>
            <TableHead>Error</TableHead>
            <TableHead>URL</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id}>
              <TableCell className="max-w-xs truncate">
                {item.papers?.title || 'Unknown Paper'}
              </TableCell>
              <TableCell>
                <Badge className={getStatusColor(item.status)}>
                  {item.status}
                </Badge>
              </TableCell>
              <TableCell>
                {item.retry_count}/{item.max_retries}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatDistanceToNow(new Date(item.scheduled_at), { addSuffix: true })}
              </TableCell>
              <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                {item.error_message || '-'}
              </TableCell>
              <TableCell>
                <a 
                  href={item.paper_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                </a>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
